export const CHART_COLORS = ["#2E90FA", "#F97066", "#12B76A", "#F79009"];

export const PIE_COLORS = [
  "#0066CC",
  "#005BB5",
  "#4D5F6E",
  "#1A2025",
  "#E8ECEF",
];

export const PIE_TOP_COUNT = 5;
export const PIE_OTHERS_COLOR = "#9CA3AF";

export const formatLabelText = (label = "") =>
  label.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const niceNumber = (value) => {
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / Math.pow(10, exponent);
  let niceFraction;
  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;
  return niceFraction * Math.pow(10, exponent);
};

export const generateYTicks = (data, yKeys, minTickCount = 5) => {
  if (!data?.length || !yKeys?.length)
    return { ticks: [0, 25, 50, 75, 100], domain: [0, 100] };

  const allValues = data.flatMap((item) =>
    yKeys.map((key) => Number(item[key]) || 0),
  );
  let min = Math.min(...allValues);
  let max = Math.max(...allValues);

  if (min === max) {
    return { ticks: [min - 10, min, min + 10], domain: [min - 10, max + 10] };
  }

  const range = max - min;
  const padding = range * 0.1;
  min -= padding;
  max += padding;

  const niceRange = niceNumber(max - min);
  const step = niceNumber(niceRange / (minTickCount - 1));
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;

  const ticks = [];
  for (let val = niceMin; val <= niceMax; val += step) ticks.push(val);
  return { ticks, domain: [niceMin, niceMax] };
};

export const getDomain = (data, lines) => {
  let min = Infinity;
  let max = -Infinity;
  data.forEach((item) => {
    lines.forEach((line) => {
      const val = item[line.dataKey];
      if (val != null) {
        min = Math.min(min, val);
        max = Math.max(max, val);
      }
    });
  });
  if (min === Infinity || max === -Infinity) return [0, 100];
  const range = max - min;
  const padding = range * 0.1 || max * 0.1;
  return [Math.max(0, Math.floor(min - padding)), Math.ceil(max + padding)];
};

export const formatMultiLineGraph = (graph) => {
  if (!graph) return {};

  const xKey = graph?.xKey || "name";

  const rawYKey = graph?.yKey;
  const yKeys = Array.isArray(rawYKey) ? rawYKey : rawYKey ? [rawYKey] : [];

  const rawData = graph?.data || [];

  const chartData = rawData.map((item) => ({
    [xKey]: item[xKey],

    // Main graph values
    ...yKeys.reduce((acc, key) => {
      acc[key] = item[key];
      return acc;
    }, {}),

    // Dynamic additional fields
    ...(item.additional || {}),
  }));

  const lines = yKeys.map((key, i) => ({
    dataKey: key,
    color: CHART_COLORS[i % CHART_COLORS.length],
    dashed: i !== 0,
  }));

  const { ticks: yTicks, domain } = generateYTicks(chartData, yKeys, 6);

  const legendItems = yKeys.map((key, i) => ({
    label: formatLabelText(key),
    color: CHART_COLORS[i % CHART_COLORS.length],
    dashed: i !== 0,
  }));

  return {
    chartData,
    lines,
    legendItems,
    yTicks,
    domain,
    xKey,
  };
};

export const formatComposedChart = (graph) => {
  if (!graph)
    return {
      chartData: [],
      barKey: "",
      lineKey: "",
      areaKey: "",
      xKey: "",
    };

  const xKey = graph?.xKey;
  const barKey = graph?.yKey;
  const lineKey = graph?.line;
  const rawData = graph?.data || [];

  const chartData = rawData.map((item) => ({
    [xKey]: item[xKey],
    [barKey]: item[barKey],
    [lineKey]: item[lineKey],

    // Add all dynamic additional fields
    ...(item.additional || {}),
  }));

  return { chartData, barKey, lineKey, areaKey: null, xKey };
};

export const formatPieChart = (graph) => {
  const graphObj = graph?.data;
  if (!graphObj) return [];
  const nameKey = graph?.xKey;
  const valueKey = graph?.yKey;
  return graphObj.map((item) => ({
    name: item[nameKey],
    value: Number(item[valueKey]),
    valueKey,
    ...item.additional,
  }));
};

export const groupTopPieItemsWithOthers = (items, max = PIE_TOP_COUNT) => {
  if (!Array.isArray(items) || items.length <= max) {
    return items || [];
  }

  // Remove commas and convert to rounded number
  const parseValue = (value) => {
    return Math.round(Number(String(value).replace(/,/g, "")) || 0);
  };

  const indexed = items.map((it, i) => ({
    ...it,
    _idx: i,
  }));

  // Sort by value descending
  indexed.sort(
    (a, b) => parseValue(b.value) - parseValue(a.value) || a._idx - b._idx,
  );

  // Top items
  const top = indexed.slice(0, max).map(({ _idx, ...rest }) => ({
    ...rest,
    value: parseValue(rest.value),
  }));

  // Remaining items
  const rest = indexed.slice(max);

  // Sum remaining values
  const othersValue = rest.reduce((sum, it) => sum + parseValue(it.value), 0);

  if (othersValue <= 0) {
    return top;
  }

  const valueKey = items[0]?.valueKey;

  return [
    ...top,
    {
      name: "Others",
      value: parseValue(othersValue),
      valueKey,
      isOthers: true,
      includes: rest.map((it) => ({
        name: it.name,
        value: parseValue(it.value),
      })),
    },
  ];
};

export const formatHeatMapData = (graph) => {
  const raw = graph?.raw || graph || {};

  const data = Array.isArray(raw.data)
    ? raw.data
    : Array.isArray(raw.rows)
      ? raw.rows
      : [];

  const rowKey = graph?.yKey || "Line";

  const rowHeaderTitle =
    [graph?.xKey, graph?.yKey].filter(Boolean).join(" \\ ") || rowKey;

  const columns =
    Array.isArray(raw.columns) && raw.columns.length
      ? raw.columns
      : data.length
        ? Object.keys(data[0] || {}).filter((k) => k !== rowKey)
        : [];

  return {
    columns,
    data: data.map((row) => {
      const rowLabel = row?.[rowKey] ?? "-";

      const values = columns.reduce((acc, col) => {
        let val = row?.[col];

        if (val === null || val === undefined || Number.isNaN(val)) {
          val = "-";
        }

        acc[col] = val;
        return acc;
      }, {});

      return {
        row: rowLabel,
        values,
      };
    }),
    rowHeaderTitle,
  };
};
